import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, CircularProgress, FormControl, InputLabel,
  Select, OutlinedInput, Stack, Alert, Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { awardService } from '../services/api';
import api from '../services/api';
import { formatDate } from '../utils/dateFormatter';
import { useAuth } from '../context/AuthContext';

const AWARD_TYPES = [
  { value: 'Star Performer', icon: '🌟' },
  { value: 'Best Director', icon: '🎬' },
  { value: 'Best Editor', icon: '✂️' },
  { value: 'Best Actor', icon: '🎭' },
  { value: 'Best Cinematographer', icon: '🎥' },
  { value: 'Innovation Award', icon: '💡' },
  { value: 'Team Player', icon: '🤝' },
  { value: 'Quick Turnaround', icon: '⚡' },
  { value: 'Goal Achiever', icon: '🎯' },
  { value: 'Growth Champion', icon: '📈' },
  { value: 'Attendance Award', icon: '🏅' },
  { value: 'Special Recognition', icon: '⭐' }
];

function Awards() {
  const { user } = useAuth();
  const [awards, setAwards] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [giveAwardDialog, setGiveAwardDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, award: null });
  const [detailsDialog, setDetailsDialog] = useState({ open: false, award: null });
  const [employees, setEmployees] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form state
  const [formData, setFormData] = useState({
    awardType: '',
    employeeIds: [],
    awardDate: new Date().toISOString().split('T')[0],
    description: '',
    cashAmount: ''
  });

  // Filters
  const [filters, setFilters] = useState({
    awardType: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    paymentStatus: ''
  });

  useEffect(() => {
    fetchEmployees();
    fetchAwards();
    fetchStatistics();
  }, [filters, user]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/users', { params: { isActive: true } });
      const allUsers = response.data.data?.users || [];
      const filteredEmployees = allUsers
        .filter(emp => emp.username !== user?.username)
        .map(emp => ({ username: emp.username, name: emp.fullName || emp.username, employeeId: emp.employeeId }));
      setEmployees(filteredEmployees.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchAwards = async () => {
    setLoading(true);
    try {
      // Check if user is HR, Manager, or Owner
      const canSeeAllAwards = ['owner', 'manager', 'hr'].includes(user?.role);
      
      const params = {
        awardType: filters.awardType,
        month: filters.month,
        year: filters.year,
        paymentStatus: filters.paymentStatus
      };

      let response;
      if (canSeeAllAwards) {
        // HR, Manager, Owner see all awards
        response = await awardService.getAll(params);
        setAwards(response.data.data.awards);
      } else {
        // Other employees only see their own awards
        response = await awardService.getEmployeeAwards(user?.username);
        // Apply filters on client side for employee's own awards
        let employeeAwards = response.data.data.awards;
        
        // Filter by award type
        if (filters.awardType) {
          employeeAwards = employeeAwards.filter(award => award.awardType === filters.awardType);
        }
        
        // Filter by month/year
        if (filters.month && filters.year) {
          employeeAwards = employeeAwards.filter(award => {
            const awardDate = new Date(award.awardDate);
            return awardDate.getMonth() + 1 === parseInt(filters.month) && 
                   awardDate.getFullYear() === parseInt(filters.year);
          });
        }
        
        setAwards(employeeAwards);
      }
    } catch (error) {
      console.error('Error fetching awards:', error);
      setSnackbar({ open: true, message: 'Error loading awards', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      // Check if user is HR, Manager, or Owner
      const canSeeAllAwards = ['owner', 'manager', 'hr'].includes(user?.role);
      
      if (canSeeAllAwards) {
        // HR, Manager, Owner see all statistics
        const response = await awardService.getStatistics({
          month: filters.month,
          year: filters.year
        });
        setStatistics(response.data.data);
      } else {
        // Other employees see only their own statistics
        const response = await awardService.getEmployeeAwards(user?.username);
        const employeeAwards = response.data.data.awards;
        
        // Calculate statistics for employee's own awards
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        const thisMonthAwards = employeeAwards.filter(award => {
          const awardDate = new Date(award.awardDate);
          return awardDate >= monthStart && awardDate <= monthEnd;
        });
        
        const totalCash = employeeAwards.reduce((sum, award) => 
          sum + parseFloat(award.cashAmount || 0), 0
        );
        
        setStatistics({
          totalAwards: employeeAwards.length,
          thisMonthAwards: thisMonthAwards.length,
          totalCash: totalCash,
          topRecipients: [{ 
            employeeId: user?.username, 
            employeeName: user?.fullName, 
            count: employeeAwards.length 
          }]
        });
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleGiveAward = () => {
    setFormData({
      awardType: '',
      employeeIds: [],
      awardDate: new Date().toISOString().split('T')[0],
      description: '',
      cashAmount: ''
    });
    setGiveAwardDialog(true);
  };

  const handleSubmitAward = async () => {
    try {
      if (!formData.awardType || formData.employeeIds.length === 0 || !formData.description) {
        setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'warning' });
        return;
      }

      await awardService.create(formData);
      setSnackbar({ open: true, message: 'Award given successfully! 🎉', severity: 'success' });
      setGiveAwardDialog(false);
      fetchAwards();
      fetchStatistics();
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Error giving award', 
        severity: 'error' 
      });
    }
  };

  const handleEditAward = (award) => {
    setEditDialog({
      open: true,
      award: {
        ...award,
        awardDate: award.awardDate || new Date().toISOString().split('T')[0]
      }
    });
  };

  const handleUpdateAward = async () => {
    try {
      await awardService.update(editDialog.award.id, editDialog.award);
      setSnackbar({ open: true, message: 'Award updated successfully', severity: 'success' });
      setEditDialog({ open: false, award: null });
      fetchAwards();
      fetchStatistics();
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Error updating award', 
        severity: 'error' 
      });
    }
  };

  const handleDeleteAward = async (id) => {
    if (window.confirm('Are you sure you want to delete this award?')) {
      try {
        await awardService.delete(id);
        setSnackbar({ open: true, message: 'Award deleted successfully', severity: 'success' });
        fetchAwards();
        fetchStatistics();
      } catch (error) {
        setSnackbar({ open: true, message: 'Error deleting award', severity: 'error' });
      }
    }
  };

  const getAwardIcon = (awardType) => {
    const award = AWARD_TYPES.find(a => a.value === awardType);
    return award ? award.icon : '🏆';
  };

  if (loading && !statistics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          🏆 {['owner', 'manager', 'hr'].includes(user?.role) 
            ? 'Employee Awards' 
            : 'My Awards'}
        </Typography>
        {['owner', 'manager', 'hr'].includes(user?.role) && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleGiveAward}
            size="medium"
          >
            Give Award
          </Button>
        )}
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6} sm={6} md={['owner', 'manager', 'hr'].includes(user?.role) ? 3 : 4}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', height: '100%' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5" fontWeight={700}>
                  {statistics.totalAwards}
                </Typography>
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>Total Awards</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={['owner', 'manager', 'hr'].includes(user?.role) ? 3 : 4}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', height: '100%' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5" fontWeight={700}>
                  {statistics.thisMonthAwards}
                </Typography>
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>This Month</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={['owner', 'manager', 'hr'].includes(user?.role) ? 3 : 4}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', height: '100%' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5" fontWeight={700}>
                  ₹{parseFloat(statistics.totalCash).toLocaleString('en-IN')}
                </Typography>
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>Total Cash</Typography>
              </CardContent>
            </Card>
          </Grid>
          {['owner', 'manager', 'hr'].includes(user?.role) && (
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white', height: '100%' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="h5" fontWeight={700}>
                    {statistics.topRecipients?.length || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>Recipients</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Award Type"
                value={filters.awardType}
                onChange={(e) => setFilters({ ...filters, awardType: e.target.value })}
                size="small"
              >
                <MenuItem value="">All Types</MenuItem>
                {AWARD_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.icon} {type.value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Month"
                value={filters.month}
                onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                size="small"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select fullWidth label="Year"
                value={filters.year}
                onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                size="small"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <MenuItem key={year} value={year}>{year}</MenuItem>;
                })}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select fullWidth label="Payment Status"
                value={filters.paymentStatus}
                onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Awards List */}
      <Card>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
            Awards History
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ py: 1, fontSize: '0.875rem' }}><strong>Date</strong></TableCell>
                  <TableCell sx={{ py: 1, fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}><strong>Award</strong></TableCell>
                  <TableCell sx={{ py: 1, fontSize: '0.875rem' }}><strong>Recipients</strong></TableCell>
                  <TableCell sx={{ py: 1, fontSize: '0.875rem' }}><strong>Cash</strong></TableCell>
                  <TableCell sx={{ py: 1, fontSize: '0.875rem' }}><strong>Status</strong></TableCell>
                  {['owner', 'manager', 'hr'].includes(user?.role) && (
                    <>
                      <TableCell sx={{ py: 1, fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}><strong>Given By</strong></TableCell>
                      <TableCell sx={{ py: 1, fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}><strong>Description</strong></TableCell>
                      <TableCell align="center" sx={{ py: 1, fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}><strong>Actions</strong></TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {awards.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={['owner', 'manager', 'hr'].includes(user?.role) ? 8 : 4} align="center">
                      <Typography color="text.secondary" py={3}>
                        No awards found. Start recognizing your team! 🎉
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  awards.map((award) => (
                    <TableRow 
                      key={award.id} 
                      hover 
                      onClick={(e) => {
                        // Open details dialog on row click (for all users on mobile, employees always)
                        const isMobile = window.innerWidth < 960;
                        if (!['owner', 'manager', 'hr'].includes(user?.role) || isMobile) {
                          // Don't open if clicking on action buttons
                          if (!e.target.closest('button')) {
                            setDetailsDialog({ open: true, award });
                          }
                        }
                      }}
                      sx={{ 
                        cursor: 'pointer'
                      }}
                    >
                      <TableCell sx={{ py: 1, fontSize: '0.813rem' }}>
                        {formatDate(award.awardDate)}
                      </TableCell>
                      <TableCell sx={{ py: 1, display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip
                          label={`${getAwardIcon(award.awardType)} ${award.awardType}`}
                          color="primary"
                          size="small"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {award.employeeNames.map((name, idx) => (
                            <Chip
                              key={idx}
                              label={name}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        {award.cashAmount > 0 ? (
                          <Typography fontWeight={600} color="success.main" fontSize="0.875rem">
                            ₹{parseFloat(award.cashAmount).toLocaleString('en-IN')}
                          </Typography>
                        ) : (
                          <Typography color="text.secondary" fontSize="0.875rem">-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Chip
                          label={award.paymentStatus || 'Approved'}
                          size="small"
                          color={award.paymentStatus === 'Paid' ? 'success' : 'primary'}
                        />
                      </TableCell>
                      {['owner', 'manager', 'hr'].includes(user?.role) && (
                        <>
                          <TableCell sx={{ py: 1, fontSize: '0.813rem', display: { xs: 'none', md: 'table-cell' } }}>
                            {award.awardedByName}
                          </TableCell>
                          <TableCell sx={{ py: 1, display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontSize: '0.813rem' }}>
                              {award.description}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1, display: { xs: 'none', md: 'table-cell' } }}>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={award.paymentStatus === 'Paid'}
                              onClick={(e) => { e.stopPropagation(); handleEditAward(award); }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={award.paymentStatus === 'Paid'}
                              onClick={(e) => { e.stopPropagation(); handleDeleteAward(award.id); }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Give Award Dialog */}
      <Dialog
        open={giveAwardDialog}
        onClose={() => setGiveAwardDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>🏆 Give Award</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                required
                label="Award Type"
                value={formData.awardType}
                onChange={(e) => setFormData({ ...formData, awardType: e.target.value })}
              >
                {AWARD_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.icon} {type.value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Select Employees</InputLabel>
                <Select
                  multiple
                  value={formData.employeeIds}
                  onChange={(e) => setFormData({ ...formData, employeeIds: e.target.value })}
                  input={<OutlinedInput label="Select Employees" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((username) => {
                        const emp = employees.find(e => e.username === username);
                        return (
                          <Chip key={username} label={emp?.name || username} size="small" />
                        );
                      })}
                    </Box>
                  )}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.username} value={emp.username}>
                      {emp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Award Date"
                value={formData.awardDate}
                onChange={(e) => setFormData({ ...formData, awardDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                label="Description / Reason"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe why this employee(s) deserve this recognition..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Cash Amount (₹)"
                value={formData.cashAmount}
                onChange={(e) => setFormData({ ...formData, cashAmount: e.target.value })}
                placeholder="Enter cash/monetary reward amount (optional)"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGiveAwardDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmitAward} variant="contained">
            Give Award
          </Button>
        </DialogActions>
      </Dialog>

      {/* Award Details Dialog (for employees) */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, award: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>🏆 Award Details</DialogTitle>
        <DialogContent>
          {detailsDialog.award && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Award Type</Typography>
                  <Chip
                    label={`${getAwardIcon(detailsDialog.award.awardType)} ${detailsDialog.award.awardType}`}
                    color="primary"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Award Date</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatDate(detailsDialog.award.awardDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Cash Amount</Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    ₹{parseFloat(detailsDialog.award.cashAmount).toLocaleString('en-IN')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Given By</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {detailsDialog.award.awardedByName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: '#f5f5f5' }}>
                    <Typography variant="body1">
                      {detailsDialog.award.description}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog({ open: false, award: null })} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Award Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, award: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Award</DialogTitle>
        <DialogContent>
          {editDialog.award && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Award Type"
                  value={editDialog.award.awardType}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    award: { ...editDialog.award, awardType: e.target.value }
                  })}
                >
                  {AWARD_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.icon} {type.value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Award Date"
                  value={editDialog.award.awardDate}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    award: { ...editDialog.award, awardDate: e.target.value }
                  })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  value={editDialog.award.description}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    award: { ...editDialog.award, description: e.target.value }
                  })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cash Amount (₹)"
                  value={editDialog.award.cashAmount}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    award: { ...editDialog.award, cashAmount: e.target.value }
                  })}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select fullWidth label="Payment Status"
                  value={editDialog.award.paymentStatus || 'Approved'}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    award: { ...editDialog.award, paymentStatus: e.target.value }
                  })}
                >
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, award: null })}>
            Cancel
          </Button>
          <Button onClick={handleUpdateAward} variant="contained">
            Update Award
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Awards;
